import cn from "classnames";
import { format } from "date-fns";
import type { FC } from "react";
import type { InboxMessageDto } from "shared";
import { Icon, IconSVG } from "../common/Icon";
import { InboxAvatar } from "./InboxAvatar";
import "./InboxMessage.css";

interface InboxMessageProps {
	message: InboxMessageDto;
	onToggleHidden?: (hidden: boolean) => void;
	isModerating?: boolean;
	onSuggestReply?: () => void;
	isSuggesting?: boolean;
}

export const InboxMessage: FC<InboxMessageProps> = ({
	message,
	onToggleHidden,
	isModerating,
	onSuggestReply,
	isSuggesting,
}) => {
	const isFromUser = message.isFromUser;

	return (
		<div
			className={cn("inbox-message", {
				"inbox-message--sent": isFromUser,
				"inbox-message--received": !isFromUser,
				"inbox-message--hidden": message.isHidden,
			})}
		>
			{!isFromUser && (
				<InboxAvatar
					url={message.senderAvatarUrl}
					label={message.senderName || message.senderUsername || "User"}
					size={32}
				/>
			)}

			<div className="inbox-message-content">
				{!isFromUser && (
					<span className="inbox-message-sender">
						{message.senderName || message.senderUsername || "Unknown"}
					</span>
				)}

				<div className="inbox-message-bubble">
					<p className="inbox-message-text">{message.content}</p>

					{message.attachments && message.attachments.length > 0 && (
						<div className="inbox-message-attachments">
							{message.attachments.map((attachment) => (
								<a
									key={attachment.url}
									href={attachment.url}
									target="_blank"
									rel="noopener noreferrer"
									className="inbox-message-attachment"
								>
									{attachment.type === "image" ? (
										<img
											src={attachment.thumbnailUrl || attachment.url}
											alt="Attachment"
										/>
									) : (
										<span className="inbox-message-attachment-file">
											<Icon
												name={
													attachment.type === "video"
														? "videocam"
														: "attachment"
												}
												size={14}
											/>
											<span>
												{attachment.type === "video" ? "Video" : "File"}
											</span>
										</span>
									)}
								</a>
							))}
						</div>
					)}
				</div>

				<span className="inbox-message-time">
					{format(new Date(message.sentAt), "h:mm a")}
					{message.isHidden && " · Hidden on TikTok"}
				</span>

				{(onToggleHidden || onSuggestReply) && (
					<div className="inbox-message-actions">
						{onSuggestReply && (
							<button
								type="button"
								className="inbox-message-moderate"
								disabled={isSuggesting}
								onClick={onSuggestReply}
							>
								<Icon name={IconSVG.Sparkle} size={14} />
								<span>{isSuggesting ? "Drafting…" : "Suggest reply"}</span>
							</button>
						)}
						{onToggleHidden && (
							<button
								type="button"
								className="inbox-message-moderate"
								disabled={isModerating}
								onClick={() => onToggleHidden(!message.isHidden)}
							>
								<Icon
									name={message.isHidden ? "visibility" : "visibility_off"}
									size={14}
								/>
								<span>{message.isHidden ? "Unhide" : "Hide"}</span>
							</button>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
